<template>
    <aside
        class="relative bg-nav-bg border-r border-r-nav-border shrink-0 hidden md:flex flex-col items-start overflow-hidden h-screen max-h-screen pt-1 pb-3"
        :style="{ width: `${renderedWidth}px` }">
        <div v-if="!userDetails?.is_anonymous" class="grow flex min-h-0 w-full">
            <div v-if="pane" class="overflow-hidden flex flex-col grow pt-4" :class="[sidebarPadding]">
                <div class="border-b border-b-nav-pane-divider shrink-0 pb-2">
                    <BaseButton type="button" theme="tertiary" size="sm" :title="t('navigation.sidebar.return')" @click="clearPane">
                        <span class="bi bi-chevron-left mr-1" aria-hidden="true"></span>
                        <span v-text="pane.title"></span>
                    </BaseButton>
                </div>
                <div class="grow overflow-hidden flex flex-col">
                    <div v-if="loading" class="flex p-4 w-full justify-center">
                        <div class="w-8 h-8 text-nav-loading">
                            <LoadingSpinner />
                        </div>
                    </div>
                    <BaseNotification v-else-if="error" theme="error" hide-dismiss class="m-4" :text="t('navigation.sidebar.loadFailed')" />
                    <div v-else-if="paneComponent" class="flex flex-col grow overflow-y-auto">
                        <component :is="paneComponent.component" v-bind="paneComponent.props" />
                    </div>
                </div>
            </div>
            <!-- Use v-show instead of v-if to keep MainMenu's internal state -->
            <MainMenu
                v-show="!pane"
                :enabled-panes="mainMenuPanes"
                :panes-with-notifications="panesWithNotifications"
                :pane-notification-counts="paneNotificationCounts"
                :sidebar-padding="sidebarPadding"
                :loading-work-panes="!panesReady"
                :show-labels="showLabels"
                @open-pane="openPane" />
        </div>

        <HorizontalDivider as="hr" spacing="none" class="border-nav-divider mb-2" />
        <div :class="[sidebarPadding]" class="w-full shrink-0 flex flex-col">
            <MainMenuRow
                v-if="userDetails?.nadia_tutorial_enabled"
                :title="t('navigation.tools.nadiaTutorial')"
                icon="bi-question-circle"
                href="/nadia-tutorial/"
                test-id="nav-nadia-tutorial"
                :show-label="showLabels"
                @click="logNadiaTutorialClick" />
            <PrivacyRow :show-label="showLabels" />
        </div>
        <HorizontalDivider as="hr" spacing="none" class="border-nav-divider my-2" />
        <div :class="[sidebarPadding]" class="w-full shrink-0">
            <AccountMenu :show-label="showLabels" />
        </div>
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
        <div
            role="separator"
            aria-orientation="vertical"
            :aria-label="t('navigation.sidebar.resize')"
            tabindex="0"
            data-testid="sidebar-resize-handle"
            :aria-valuenow="sidebarWidth"
            :aria-valuemin="MIN_SIDEBAR_WIDTH"
            :aria-valuemax="MAX_SIDEBAR_WIDTH"
            class="sidebar-resize-handle"
            @pointerdown="startResize"
            @keydown="handleResizeKeydown" />
    </aside>
</template>

<script setup lang="ts">
import { useMediaQuery, useStorage } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { useNadiaTutorialAnalytics } from "~vue/composables/useNadiaTutorialAnalytics";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { type Component, computed, inject, onMounted, type Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import AccountMenu from "./AccountMenu.vue";
import MainMenu from "./MainMenu.vue";
import MainMenuRow from "./MainMenuRow.vue";
import { ALL_PANES, MainMenuPane, type PaneNotificationCounts, validateDisabledPanes } from "./menuPanes";
import PrivacyRow from "./PrivacyRow.vue";

const { t } = useI18n();

interface MenuPane {
    id: string;
    title: string;
    component?: Component;
}

interface PaneComponentSlot {
    component: Component;
    props: Record<string, unknown>;
}

interface Props {
    panesWithNotifications?: MainMenuPane[];
    paneNotificationCounts?: PaneNotificationCounts;
}

withDefaults(defineProps<Props>(), {
    panesWithNotifications: () => [],
    paneNotificationCounts: () => ({}),
});

const disabledPanes = ref<MainMenuPane[]>([
    MainMenuPane.GUIDED_SETUP,
    MainMenuPane.COLLABORATION_PROFILE,
    MainMenuPane.CONNECTIONS,
    MainMenuPane.JOURNEYS,
    MainMenuPane.DEVELOPMENT_JOURNEYS,
    MainMenuPane.MY_DEVELOPMENT,
    MainMenuPane.MY_WORK,
    MainMenuPane.PERFORMANCE_MANAGEMENT,
    MainMenuPane.COMPETENCY_ASSESSMENT,
    MainMenuPane.SCHNEIDER_ASSESSMENT,
    MainMenuPane.REFLECTIONS,
]);
const pane = ref<MenuPane | null>(null);
const paneComponent = ref<PaneComponentSlot | null>(null);
const error = ref<Record<string, unknown> | null>(null);

const navigationUrl = ref("/navigation/");
const {
    data: navigationData,
    error: navigationError,
    isFetching: loading,
    execute: fetchNavigation,
} = useFetch(navigationUrl, { immediate: false }).get().json<Record<string, unknown>>();

const userDetails = inject<Ref<UserDetails>>("user_details");
const isDesktop = useMediaQuery("(min-width: 768px)");
const { logLinkViewed, logTutorialEvent } = useNadiaTutorialAnalytics();

const panesReady = ref(false);

const mainMenuPanes = computed(() => ALL_PANES.filter((p) => !disabledPanes.value.includes(p)));

const SIDEBAR_WIDTH_STORAGE_KEY = "sidebar-width";
const DEFAULT_SIDEBAR_WIDTH = 280;
// Icon-only rail: 8px sidebar padding + one nav row (14px row padding + 24px icon box) per side, no label.
const MIN_SIDEBAR_WIDTH = 68;
// Narrowest width at which labels still fit; below this the sidebar snaps to the icon-only rail.
const MIN_LABELED_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 480;
const RESIZE_KEY_STEP = 16;

function clampWidth(width: number): number {
    const rounded = Math.round(width);
    if (rounded < MIN_LABELED_SIDEBAR_WIDTH) {
        return rounded < (MIN_SIDEBAR_WIDTH + MIN_LABELED_SIDEBAR_WIDTH) / 2 ? MIN_SIDEBAR_WIDTH : MIN_LABELED_SIDEBAR_WIDTH;
    }
    return Math.min(MAX_SIDEBAR_WIDTH, rounded);
}

const sidebarWidth = useStorage(SIDEBAR_WIDTH_STORAGE_KEY, DEFAULT_SIDEBAR_WIDTH);
sidebarWidth.value = Number.isFinite(sidebarWidth.value) && sidebarWidth.value > 0 ? clampWidth(sidebarWidth.value) : DEFAULT_SIDEBAR_WIDTH;
const showLabels = computed(() => sidebarWidth.value >= MIN_LABELED_SIDEBAR_WIDTH);
const sidebarPadding = computed(() => (showLabels.value ? "px-5" : "px-2"));
// An open pane renders real content, so it always gets at least the labeled width.
const renderedWidth = computed(() => (pane.value ? Math.max(sidebarWidth.value, MIN_LABELED_SIDEBAR_WIDTH) : sidebarWidth.value));

function startResize(event: PointerEvent) {
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    const offset = sidebarWidth.value - event.clientX;
    const onMove = (move: PointerEvent) => {
        sidebarWidth.value = clampWidth(move.clientX + offset);
    };
    const onUp = () => {
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", onUp);
        handle.removeEventListener("pointercancel", onUp);
        document.body.classList.remove("sidebar-resizing");
    };
    handle.setPointerCapture(event.pointerId);
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
    handle.addEventListener("pointercancel", onUp);
    document.body.classList.add("sidebar-resizing");
}

function handleResizeKeydown(event: KeyboardEvent) {
    const targets: Record<string, number> = {
        ArrowLeft: sidebarWidth.value > MIN_LABELED_SIDEBAR_WIDTH ? sidebarWidth.value - RESIZE_KEY_STEP : MIN_SIDEBAR_WIDTH,
        ArrowRight: showLabels.value ? sidebarWidth.value + RESIZE_KEY_STEP : MIN_LABELED_SIDEBAR_WIDTH,
        Home: MIN_SIDEBAR_WIDTH,
        End: MAX_SIDEBAR_WIDTH,
    };
    if (!(event.key in targets)) {
        return;
    }
    event.preventDefault();
    sidebarWidth.value = clampWidth(targets[event.key]);
}

function clearPane() {
    pane.value = null;
}

function logNadiaTutorialClick() {
    logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_LINK_CLICKED, { entry_source: "sidebar" });
}

watch(
    () => isDesktop.value && userDetails?.value?.nadia_tutorial_enabled,
    (visible) => {
        if (visible) {
            logLinkViewed("sidebar", showLabels.value);
        }
    },
    { immediate: true },
);

async function openPane(p: MenuPane) {
    pane.value = p;

    error.value = null;
    paneComponent.value = null;
    navigationUrl.value = `/navigation/?name=${p.id}`;
    await fetchNavigation();

    if (navigationError.value) {
        error.value = navigationData.value ?? (navigationError.value as Record<string, unknown>);
        return;
    }

    if (p.component && navigationData.value) {
        paneComponent.value = { component: p.component, props: navigationData.value };
    }
}

onMounted(async () => {
    disabledPanes.value = await validateDisabledPanes(disabledPanes.value);
    setTimeout(() => {
        panesReady.value = true;
    }, 300);
});
</script>
